import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { toast } from '@/lib/toast';
import { findInvestmentProduct } from '@/editor/investmentProduct';
import {
  emptyTableRowMatchingTable,
  getPricingTypeForTableInGroup,
  getTableChildIndexInGroup,
  rowHasHeaderCell,
} from '@/editor/investmentTableRow';
import {
  createRowDragSorter,
  reorderRowsForDrop,
  type RowDragSorter,
} from '@/lib/rowDragSort';

const pluginKey = new PluginKey('investmentRowDelete');

function countDataRowsInTable(tableNode: PMNode): number {
  let n = 0;
  for (let i = 0; i < tableNode.childCount; i++) {
    const row = tableNode.child(i);
    if (row.type.name !== 'tableRow') continue;
    let isHeader = false;
    row.forEach((cell: PMNode) => {
      if (cell.type.name === 'tableHeader') isHeader = true;
    });
    if (!isHeader) n++;
  }
  return n;
}

/**
 * Bepaalt de tableRow + parent table vanaf een documentpositie (actuele state).
 * Voorkomt verwijderen op basis van verouderde posities uit widget-closures (kan heel
 * document of verkeerde node raken).
 */
function findTableRowAndTableAtPos(
  doc: PMNode,
  pos: number,
): {
  rowFrom: number;
  rowTo: number;
  tableFrom: number;
  tableNode: PMNode;
  groupNode: PMNode;
  tableChildIndex: number;
} | null {
  if (pos < 0 || pos > doc.content.size) return null;
  const $pos = doc.resolve(pos);
  let groupDepth = -1;
  for (let g = 1; g <= $pos.depth; g++) {
    if ($pos.node(g).type.name === 'investmentGroupNode') {
      groupDepth = g;
      break;
    }
  }
  if (groupDepth < 0) return null;

  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (node.type.name !== 'tableRow') continue;
    if (rowHasHeaderCell(node)) continue;
    const tableDepth = d - 1;
    if (tableDepth < 1) continue;
    const parent = $pos.node(tableDepth);
    if (parent.type.name !== 'table') continue;
    const tableFrom = $pos.before(tableDepth);
    const rowFrom = $pos.before(d);
    const rowTo = rowFrom + node.nodeSize;
    const groupNode = $pos.node(groupDepth);
    const tableChildIndex = getTableChildIndexInGroup(groupNode, parent);
    if (tableChildIndex < 0) return null;
    return { rowFrom, rowTo, tableFrom, tableNode: parent, groupNode, tableChildIndex };
  }
  return null;
}

/**
 * Eerste positie *binnen* het eerste niet-lege blok van de eerste cel.
 * Lege paragrafen worden overgeslagen — anders komt de widget op een eigen regel
 * boven een bulletList (witte regel + icoon los van de inhoud).
 */
function getWidgetDocumentPos(rowPos: number, rowNode: PMNode): number {
  let pos = rowPos + 1;
  if (rowNode.childCount < 1) return pos;
  const cell = rowNode.child(0);
  if (cell.type.name !== 'tableCell') return pos;
  pos += 1;
  if (cell.childCount < 1) return pos;
  // pos = eerste positie vóór het eerste blok in de cel (rowPos + 2)

  for (let i = 0; i < cell.childCount; i++) {
    const block = cell.child(i);
    const emptyParagraph =
      block.type.name === 'paragraph' && block.content.size === 0 && i < cell.childCount - 1;
    if (emptyParagraph) {
      pos += block.nodeSize;
      continue;
    }
    pos += 1;
    return pos;
  }
  return pos;
}

const ICON_MINUS =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg>';
/** Twee rechthoeken: dupliceren / kopiëren */
const ICON_DUPLICATE =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
const ICON_PLUS =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';
const ICON_UP =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m18 15-6-6-6 6"/></svg>';
const ICON_DOWN =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';
const ICON_MORE =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>';
const ICON_GRIP =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/></svg>';

function dataRowRanges(tableFrom: number, tableNode: PMNode) {
  const ranges: { from: number; to: number; node: PMNode }[] = [];
  let rowPos = tableFrom + 1;
  for (let i = 0; i < tableNode.childCount; i++) {
    const row = tableNode.child(i);
    if (row.type.name === 'tableRow' && !rowHasHeaderCell(row)) {
      ranges.push({ from: rowPos, to: rowPos + row.nodeSize, node: row });
    }
    rowPos += row.nodeSize;
  }
  return ranges;
}

/**
 * Verplaatst één datarij binnen zijn tabel. Een cursor in een van de rijen verhuist mee,
 * zodat het geselecteerde product (en het paneel rechts) geselecteerd blijft.
 */
function moveDataRow(
  editor: Editor,
  tableFrom: number,
  sourceFrom: number,
  targetFrom: number,
  insertAfter: boolean,
): boolean {
  const tableNode = editor.state.doc.nodeAt(tableFrom);
  if (!tableNode || tableNode.type.name !== 'table') return false;

  const rows = dataRowRanges(tableFrom, tableNode);
  const sourceIndex = rows.findIndex((row) => row.from === sourceFrom);
  const targetIndex = rows.findIndex((row) => row.from === targetFrom);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return false;

  const order = reorderRowsForDrop(rows.map((_, index) => index), sourceIndex, targetIndex, insertAfter);
  const { selection } = editor.state;
  const selected = rows.findIndex((row) => selection.from > row.from && selection.from < row.to);
  const tr = editor.state.tr.replaceWith(
    rows[0].from,
    rows[rows.length - 1].to,
    order.map((index) => rows[index].node),
  );
  if (selected >= 0) {
    let pos = rows[0].from;
    for (const index of order) {
      if (index === selected) break;
      pos += rows[index].node.nodeSize;
    }
    tr.setSelection(TextSelection.near(tr.doc.resolve(pos + selection.from - rows[selected].from)));
  }
  editor.view.dispatch(tr);
  return true;
}

/** Schuift de rij rond `pos` één plek omhoog of omlaag (menu en pijltjestoetsen). */
export function moveInvestmentRow(editor: Editor, pos: number, direction: -1 | 1): boolean {
  const rowInfo = findTableRowAndTableAtPos(editor.state.doc, pos);
  if (!rowInfo) return false;
  const rows = dataRowRanges(rowInfo.tableFrom, rowInfo.tableNode);
  const target = rows[rows.findIndex((row) => row.from === rowInfo.rowFrom) + direction];
  return target ? moveDataRow(editor, rowInfo.tableFrom, rowInfo.rowFrom, target.from, direction === 1) : false;
}

/** Rij-identiteit voor de sorteerhulp: positie van de rij plus de tabel eromheen. */
type InvestmentRowRef = { rowFrom: number; tableFrom: number };

type RowDragController = RowDragSorter<InvestmentRowRef> & {
  pointerDown: (event: PointerEvent, wrap: HTMLElement, widgetAnchorPos: number) => void;
};

/**
 * Sorteren van investeringsrijen. De mechaniek (kaartje, invoeglijn, window-listeners)
 * komt uit `createRowDragSorter`; hier vertalen we alleen tussen DOM en documentposities.
 */
export function createRowDragController(editor: Editor): RowDragController {
  /**
   * Zoekt de actuele <tr> bij een documentpositie, zodat een hertekening tijdens het
   * slepen geen losgekoppeld element achterlaat. nodeDOM lukt niet altijd binnen de
   * table-nodeview; domAtPos op een positie *in* de rij is de betrouwbare achtervang.
   */
  const rowElementAtPos = (rowFrom: number): HTMLElement | null => {
    try {
      const nodeDom = editor.view.nodeDOM(rowFrom);
      if (nodeDom instanceof HTMLElement && nodeDom.tagName === 'TR') return nodeDom;
    } catch {
      /* positie hoort (nog) niet bij een getekende node */
    }

    try {
      const { node } = editor.view.domAtPos(rowFrom + 1);
      const element = node instanceof HTMLElement ? node : node.parentElement;
      const row = element?.closest('tr');
      return row instanceof HTMLElement ? row : null;
    } catch {
      return null;
    }
  };

  const rowInfoFromElement = (rowElement: HTMLElement) => {
    const groupId = rowElement.querySelector<HTMLElement>('[data-product-group]')?.dataset.productGroup;
    if (groupId) {
      const product = findInvestmentProduct(editor, groupId);
      if (product) return findTableRowAndTableAtPos(editor.state.doc, product.pos + 3);
    }
    const rowWidget = rowElement.querySelector<HTMLElement>('.investment-row-actions-wrap');
    const storedAnchor = Number(rowWidget?.dataset.investmentRowAnchor);
    if (Number.isFinite(storedAnchor)) {
      const fromStoredAnchor = findTableRowAndTableAtPos(editor.state.doc, storedAnchor);
      if (fromStoredAnchor) return fromStoredAnchor;
    }

    try {
      // De positie van een <tr> zelf valt vaak precies vóór de row node. Een celpositie
      // ligt wél binnen de row en is daarom een veilige fallback voor ProseMirror.
      const cell = rowElement.querySelector('td');
      const pos = editor.view.posAtDOM(cell || rowElement, 0);
      return findTableRowAndTableAtPos(editor.state.doc, pos);
    } catch {
      return null;
    }
  };

  const cellText = (cell: Element | undefined) => {
    if (!cell) return '';
    const clone = cell.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return cell.textContent?.trim() || '';
    clone.querySelectorAll('.investment-row-actions-wrap').forEach((node) => node.remove());
    return clone.textContent?.trim() || '';
  };

  const sorter = createRowDragSorter<InvestmentRowRef>({
    rowSelector: 'tr',
    gutter: 40,

    resolve: (rowElement) => {
      if (!editor.view.dom.contains(rowElement)) return null;
      const info = rowInfoFromElement(rowElement);
      return info ? { rowFrom: info.rowFrom, tableFrom: info.tableFrom } : null;
    },

    sameRow: (a, b) => a.rowFrom === b.rowFrom,

    canDrop: (source, target) => source.tableFrom === target.tableFrom,

    element: (row) => rowElementAtPos(row.rowFrom),

    ghost: (row) => {
      const rowElement = rowElementAtPos(row.rowFrom);
      const productHeading = rowElement?.querySelector('.investment-product-heading');
      if (productHeading) return {
        title: productHeading.querySelector('strong')?.textContent || 'Product',
        details: productHeading.querySelector('.investment-product-amount')?.textContent || '',
      };
      // Klantpreview: de rij toont omschrijving en prijsopbouw, het kaartje alleen de naam.
      const product = editor.state.doc.nodeAt(row.rowFrom)?.attrs.investmentProduct;
      if (product) return { title: product.productName || 'Product' };
      const cells = rowElement ? Array.from(rowElement.querySelectorAll('td')) : [];
      return {
        title: cellText(cells[0]) || 'Prijsregel',
        details: cells
          .slice(1)
          .map((cell) => cellText(cell))
          .filter(Boolean)
          .join(' · '),
      };
    },

    commit: (source, target, insertAfter) => {
      moveDataRow(editor, source.tableFrom, source.rowFrom, target.rowFrom, insertAfter);
    },
  });

  return {
    ...sorter,

    pointerDown(event, wrap, widgetAnchorPos) {
      const pos = wrap.dataset.productGroup ? widgetAnchorPos : resolveDocPosForRowAction(editor, wrap, widgetAnchorPos, event);
      const rowInfo = pos == null ? null : findTableRowAndTableAtPos(editor.state.doc, pos);
      if (!rowInfo) return;

      // Eén datarij valt nergens heen; laat de klik dan met rust.
      if (dataRowRanges(rowInfo.tableFrom, rowInfo.tableNode).length < 2) return;

      sorter.start(event, { rowFrom: rowInfo.rowFrom, tableFrom: rowInfo.tableFrom });
    },
  };
}

function resolveDocPosFromWidget(view: Editor['view'], root: HTMLElement): number | null {
  const tryPos = (el: Node | null) => {
    if (!el || !(el instanceof HTMLElement)) return null;
    try {
      const p = view.posAtDOM(el, 0);
      if (typeof p === 'number' && p >= 0) return p;
    } catch {
      /* posAtDOM throws if node not under editor */
    }
    return null;
  };
  const buttons = root.querySelectorAll('button');
  for (let i = 0; i < buttons.length; i++) {
    const p = tryPos(buttons[i]);
    if (p != null) return p;
  }
  return tryPos(root);
}

/**
 * 1) posAtDOM op widget, 2) posAtCoords (klik), 3) decoration-anchor, 4) selection als die in dezelfde rij zit.
 */
function resolveDocPosForRowAction(
  editor: Editor,
  wrap: HTMLElement,
  widgetAnchorPos: number,
  e: MouseEvent,
): number | null {
  const view = editor.view;
  const doc = editor.state.doc;

  const fromDom = resolveDocPosFromWidget(view, wrap);
  if (fromDom != null) return fromDom;

  const coords = view.posAtCoords({ left: e.clientX, top: e.clientY });
  if (coords != null && typeof coords.pos === 'number' && coords.pos >= 0 && coords.pos <= doc.content.size) {
    return coords.pos;
  }

  const clamped = Math.max(0, Math.min(widgetAnchorPos, doc.content.size));
  const fromAnchor = findTableRowAndTableAtPos(doc, clamped);
  if (fromAnchor != null) return clamped;

  const anchorPos = editor.state.selection.$anchor.pos;
  if (anchorPos < 0 || anchorPos > doc.content.size) return null;
  const rowSel = findTableRowAndTableAtPos(doc, anchorPos);
  const rowWidget = findTableRowAndTableAtPos(doc, Math.max(0, Math.min(widgetAnchorPos, doc.content.size)));
  if (
    rowSel &&
    rowWidget &&
    rowSel.rowFrom === rowWidget.rowFrom &&
    rowSel.rowTo === rowWidget.rowTo
  ) {
    return anchorPos;
  }

  return null;
}

function createRowActionsWidget(
  widgetAnchorPos: number,
  rowPos: number,
  dataRowCountInTable: number,
  dataRowIndex: number,
  editor: Editor,
  dragController: RowDragController,
): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'investment-row-actions-wrap';
  wrap.setAttribute('contenteditable', 'false');
  wrap.dataset.investmentRowAnchor = String(widgetAnchorPos);
  wrap.dataset.investmentRowFrom = String(rowPos);

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'investment-row-action-trigger';
  trigger.title = 'Rijacties';
  trigger.setAttribute('aria-label', 'Rijacties openen');
  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.innerHTML = ICON_MORE;

  const dragHandle = document.createElement('button');
  dragHandle.type = 'button';
  dragHandle.className = 'investment-row-drag-handle';
  dragHandle.title = dataRowCountInTable > 1
    ? 'Sleep om de rij te verplaatsen · pijltjestoetsen werken ook'
    : 'Voeg nog een rij toe om te sorteren';
  dragHandle.setAttribute('aria-label', 'Rij verslepen');
  dragHandle.disabled = dataRowCountInTable <= 1;
  dragHandle.innerHTML = ICON_GRIP;

  const menu = document.createElement('span');
  menu.className = 'investment-row-actions-menu';
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'Rijacties');

  const btnUp = document.createElement('button');
  btnUp.type = 'button';
  btnUp.className = 'investment-row-action-btn investment-row-action-move';
  btnUp.title = 'Rij omhoog';
  btnUp.setAttribute('aria-label', 'Rij omhoog');
  btnUp.setAttribute('role', 'menuitem');
  btnUp.innerHTML = `${ICON_UP}<span>Omhoog</span>`;
  btnUp.disabled = dataRowIndex <= 0;

  const btnDown = document.createElement('button');
  btnDown.type = 'button';
  btnDown.className = 'investment-row-action-btn investment-row-action-move';
  btnDown.title = 'Rij omlaag';
  btnDown.setAttribute('aria-label', 'Rij omlaag');
  btnDown.setAttribute('role', 'menuitem');
  btnDown.innerHTML = `${ICON_DOWN}<span>Omlaag</span>`;
  btnDown.disabled = dataRowIndex >= dataRowCountInTable - 1;

  const btnMinus = document.createElement('button');
  btnMinus.type = 'button';
  btnMinus.className = 'investment-row-action-btn investment-row-action-minus';
  btnMinus.title = 'Deze rij verwijderen';
  btnMinus.setAttribute('aria-label', 'Rij verwijderen');
  btnMinus.setAttribute('role', 'menuitem');
  btnMinus.innerHTML = `${ICON_MINUS}<span>Verwijderen</span>`;

  const disabledMinus = dataRowCountInTable <= 1;
  btnMinus.disabled = disabledMinus;
  if (disabledMinus) {
    btnMinus.title = 'Minimaal één data-regel verplicht';
  }

  const btnDuplicate = document.createElement('button');
  btnDuplicate.type = 'button';
  btnDuplicate.className = 'investment-row-action-btn investment-row-action-duplicate';
  btnDuplicate.title = 'Rij dupliceren';
  btnDuplicate.setAttribute('aria-label', 'Rij dupliceren');
  btnDuplicate.setAttribute('role', 'menuitem');
  btnDuplicate.innerHTML = `${ICON_DUPLICATE}<span>Dupliceren</span>`;

  const btnPlus = document.createElement('button');
  btnPlus.type = 'button';
  btnPlus.className = 'investment-row-action-btn investment-row-action-plus';
  btnPlus.title = 'Rij hieronder invoegen';
  btnPlus.setAttribute('aria-label', 'Rij hieronder invoegen');
  btnPlus.setAttribute('role', 'menuitem');
  btnPlus.innerHTML = `${ICON_PLUS}<span>Rij eronder</span>`;

  const handleMouseDown = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  let menuOpen = false;
  let openTableWrapper: Element | null = null;
  const setMenuOpen = (open: boolean) => {
    menuOpen = open;
    wrap.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', String(open));
    if (open) {
      openTableWrapper = wrap.closest('.tableWrapper');
      openTableWrapper?.classList.add('is-row-menu-open');
    } else {
      const tableWrapper = openTableWrapper;
      requestAnimationFrame(() => {
        if (!tableWrapper?.querySelector('.investment-row-actions-wrap.is-open')) {
          tableWrapper?.classList.remove('is-row-menu-open');
        }
      });
      openTableWrapper = null;
    }
  };

  trigger.addEventListener('mousedown', (e) => {
    e.stopPropagation();
  });
  trigger.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    trigger.focus();
    setMenuOpen(!menuOpen);
  });
  wrap.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen(false);
    trigger.focus();
  });
  wrap.addEventListener('focusout', () => {
    requestAnimationFrame(() => {
      if (!wrap.contains(document.activeElement)) setMenuOpen(false);
    });
  });

  dragHandle.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
  dragHandle.addEventListener('pointerdown', (e) => {
    setMenuOpen(false);
    dragController.pointerDown(e, wrap, widgetAnchorPos);
  });

  const handleMoveClick = (direction: -1 | 1) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const pos = resolveDocPosForRowAction(editor, wrap, widgetAnchorPos, e);
    if (pos == null) {
      toast.error('Kon positie niet bepalen. Klik opnieuw.');
      return;
    }

    if (!findTableRowAndTableAtPos(editor.state.doc, pos)) {
      toast.error('Kon de rij niet vinden. Probeer opnieuw.');
      return;
    }

    if (moveInvestmentRow(editor, pos, direction)) setMenuOpen(false);
  };

  const handleMinusClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const pos = resolveDocPosForRowAction(editor, wrap, widgetAnchorPos, e);
    if (pos == null) {
      toast.error('Kon positie niet bepalen. Klik opnieuw.');
      return;
    }

    const rowInfo = findTableRowAndTableAtPos(editor.state.doc, pos);
    if (!rowInfo) {
      toast.error('Kon de rij niet vinden. Probeer opnieuw.');
      return;
    }

    const dataRows = countDataRowsInTable(rowInfo.tableNode);
    if (dataRows <= 1) {
      toast.error('Minimaal één regel is nodig (naast de kopregel).');
      return;
    }

    const { rowFrom, rowTo } = rowInfo;
    const $start = editor.state.doc.resolve(rowFrom);
    const nodeAfter = $start.nodeAfter;
    if (
      !nodeAfter ||
      nodeAfter.type.name !== 'tableRow' ||
      rowTo !== rowFrom + nodeAfter.nodeSize
    ) {
      toast.error('Kon de rij niet veilig verwijderen. Probeer opnieuw.');
      return;
    }

    setMenuOpen(false);
    // Geen focus(): die scrollt naar de oude selectie, vaak bovenaan het document.
    editor.view.dispatch(editor.state.tr.delete(rowFrom, rowTo));
  };

  const handlePlusClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const pos = resolveDocPosForRowAction(editor, wrap, widgetAnchorPos, e);
    if (pos == null) {
      toast.error('Kon positie niet bepalen. Klik opnieuw.');
      return;
    }

    const rowInfo = findTableRowAndTableAtPos(editor.state.doc, pos);
    if (!rowInfo) {
      toast.error('Kon de rij niet vinden. Probeer opnieuw.');
      return;
    }

    const pt = getPricingTypeForTableInGroup(rowInfo.groupNode, rowInfo.tableChildIndex);
    const currency = (rowInfo.groupNode.attrs.currency as string) || 'EUR';
    const rowJson = emptyTableRowMatchingTable(rowInfo.tableNode, pt, currency);
    setMenuOpen(false);
    editor.chain().focus().insertContentAt(rowInfo.rowTo, rowJson).run();
  };

  const handleDuplicateClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const pos = resolveDocPosForRowAction(editor, wrap, widgetAnchorPos, e);
    if (pos == null) {
      toast.error('Kon positie niet bepalen. Klik opnieuw.');
      return;
    }

    const rowInfo = findTableRowAndTableAtPos(editor.state.doc, pos);
    if (!rowInfo) {
      toast.error('Kon de rij niet vinden. Probeer opnieuw.');
      return;
    }

    const { rowFrom, rowTo } = rowInfo;
    const $start = editor.state.doc.resolve(rowFrom);
    const nodeAfter = $start.nodeAfter;
    if (
      !nodeAfter ||
      nodeAfter.type.name !== 'tableRow' ||
      rowTo !== rowFrom + nodeAfter.nodeSize
    ) {
      toast.error('Kon de rij niet veilig dupliceren. Probeer opnieuw.');
      return;
    }

    const rowJson = nodeAfter.toJSON();
    setMenuOpen(false);
    editor.chain().focus().insertContentAt(rowTo, rowJson).run();
  };

  btnUp.addEventListener('mousedown', handleMouseDown);
  btnUp.addEventListener('click', handleMoveClick(-1));
  btnDown.addEventListener('mousedown', handleMouseDown);
  btnDown.addEventListener('click', handleMoveClick(1));
  btnMinus.addEventListener('mousedown', handleMouseDown);
  btnMinus.addEventListener('click', handleMinusClick);
  btnDuplicate.addEventListener('mousedown', handleMouseDown);
  btnDuplicate.addEventListener('click', handleDuplicateClick);
  btnPlus.addEventListener('mousedown', handleMouseDown);
  btnPlus.addEventListener('click', handlePlusClick);

  dragHandle.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    e.stopPropagation();
    (e.key === 'ArrowUp' ? btnUp : btnDown).click();
  });

  menu.appendChild(btnUp);
  menu.appendChild(btnDown);
  menu.appendChild(btnDuplicate);
  menu.appendChild(btnPlus);
  menu.appendChild(btnMinus);
  wrap.appendChild(dragHandle);
  wrap.appendChild(trigger);
  wrap.appendChild(menu);
  return wrap;
}

function buildDecorations(doc: PMNode, editor: Editor, dragController: RowDragController): Decoration[] {
  const decs: Decoration[] = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== 'investmentGroupNode') return true;

    let offset = pos + 1;
    for (let i = 0; i < node.childCount; i++) {
      const child = node.child(i);
      if (child.type.name === 'table') {
        const dataRowCount = countDataRowsInTable(child);
        let rowPos = offset + 1;
        let dataRowIndex = 0;
        for (let j = 0; j < child.childCount; j++) {
          const row = child.child(j);
          if (row.type.name === 'tableRow') {
            if (row.attrs.investmentProduct) {
              rowPos += row.nodeSize;
              dataRowIndex++;
              continue;
            }
            if (!rowHasHeaderCell(row)) {
              const widgetPos = getWidgetDocumentPos(rowPos, row);
              const widget = createRowActionsWidget(
                widgetPos,
                rowPos,
                dataRowCount,
                dataRowIndex,
                editor,
                dragController,
              );
              decs.push(
                Decoration.widget(widgetPos, widget, {
                  side: 1,
                  key: `inv-row-act-${rowPos}`,
                  stopEvent: (event) =>
                    event.type === 'mousedown' ||
                    event.type === 'click' ||
                    event.type === 'pointerdown' ||
                    event.type === 'pointermove' ||
                    event.type === 'pointerup' ||
                    event.type === 'pointercancel' ||
                    event.type === 'keydown',
                }),
              );
              dataRowIndex++;
            }
          }
          rowPos += row.nodeSize;
        }
      }
      offset += child.nodeSize;
    }
    return false;
  });

  return decs;
}

/**
 * Toont per datarij één rustige menuknop met alle rijacties.
 */
export const InvestmentRowDelete = Extension.create({
  name: 'investmentRowDelete',

  addProseMirrorPlugins() {
    const editor = this.editor;
    const dragController = createRowDragController(editor);

    return [
      new Plugin({
        key: pluginKey,
        view() {
          return { destroy: () => dragController.destroy() };
        },
        props: {
          decorations(state) {
            if (!editor.isEditable) {
              return DecorationSet.empty;
            }
            const decs = buildDecorations(state.doc, editor, dragController);
            return DecorationSet.create(state.doc, decs);
          },
        },
      }),
    ];
  },
});
